import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { media } from "@/content/media";
import { Reveal, EASE } from "@/components/Motion";
import { V3Section, V3Eyebrow, V3Title } from "@/sections/v3/shared";

// 5. Catégories — maître-détail : liste à gauche, grand panneau sticky à droite.
// Sur mobile : accordéon vertical.

const useItems = () => {
  const cat = useContent().categories;
  return [...cat.items, { ...cat.grandPrix, grand: true }];
};

const DetailPanel = ({ item, seeAll }) => (
  <div className="relative hidden min-h-[460px] flex-col overflow-hidden border border-copper/25 p-10 lg:flex lg:p-14" data-testid="categories-panel">
    <span aria-hidden="true" className="pointer-events-none absolute top-4 end-8 select-none font-serif text-[6.5rem] leading-none text-blush/20">
      {item.n}
    </span>
    <AnimatePresence mode="wait">
      <motion.div
        key={item.n}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="relative pt-10"
      >
        {item.grand && <p className="text-[0.75rem] uppercase tracking-[0.35em] text-copper">{item.label}</p>}
        <h3 className="mt-3 max-w-md font-serif text-[1.5rem] leading-snug text-white md:text-[1.9rem]" data-testid="categories-panel-title">
          {item.title}
        </h3>
        <p className="mt-5 max-w-lg text-[0.95rem] leading-relaxed text-white/80">{item.text}</p>
        {item.grand && (
          <div className="relative mt-8 aspect-[16/8] overflow-hidden">
            <img src={media.grandPrix} alt={item.imageAlt} loading="lazy" className="photo-treat h-full w-full object-cover" />
            <div className="photo-duo absolute inset-0" aria-hidden="true" />
            <div className="photo-spot absolute inset-0" aria-hidden="true" />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
    <Link to="/categories" data-testid="categories-see-all" className="link-line mt-auto w-fit pt-10 text-[0.75rem] uppercase tracking-[0.3em] text-copper hover:text-blush">
      {seeAll} <ArrowUpRight size={13} strokeWidth={1.4} className="mb-0.5 inline" />
    </Link>
  </div>
);

const MobileAccordion = ({ items }) => {
  const [open, setOpen] = useState(-1);
  return (
    <div className="mt-12 lg:hidden" data-testid="categories-accordion">
      {items.map((it, i) => (
        <div key={it.n} className={`border-t border-copper/25 last:border-b ${it.grand ? "bg-plum/25" : ""}`}>
          <h3>
            <button
              type="button"
              onClick={() => setOpen(open === i ? -1 : i)}
              aria-expanded={open === i}
              aria-controls={`cat-panel-${i}`}
              data-testid={`cat-toggle-${it.n}`}
              className="flex w-full items-center justify-between gap-5 py-5 text-start"
            >
              <span className="flex items-baseline gap-4">
                <span className="font-sans text-[0.75rem] tabular-nums tracking-[0.18em] text-copper">{it.n}</span>
                <span className="font-serif text-[1.1rem] leading-snug text-white">{it.title}</span>
              </span>
              <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.4, ease: EASE }} className="shrink-0 text-copper">
                <Plus size={18} strokeWidth={1} />
              </motion.span>
            </button>
          </h3>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                id={`cat-panel-${i}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="overflow-hidden"
              >
                <p className="pb-6 ps-9 text-[0.95rem] leading-relaxed text-white/80">{it.text}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

export const Categories3 = () => {
  const c = useContent();
  const cat = c.categories;
  const items = useItems();
  const [active, setActive] = useState(0);

  return (
    <V3Section id={cat.id} bg="#0B0A0A" className="py-24 md:py-32">
      <V3Eyebrow>{cat.eyebrow}</V3Eyebrow>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <V3Title testid="categories-title">{cat.title}</V3Title>
        <Reveal delay={0.1}>
          <p className="font-serif text-xl italic text-copper">{cat.pageIntro}</p>
        </Reveal>
      </div>

      <div className="mt-14 hidden gap-14 lg:grid lg:grid-cols-[minmax(280px,380px)_1fr] lg:gap-20">
        {/* Maître : liste verticale des 15 titres */}
        <ul data-testid="categories-list">
          {items.map((it, i) => (
            <li key={it.n} className="border-b border-copper/20 first:border-t">
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
                aria-pressed={active === i}
                data-testid={`cat-item-${it.n}`}
                className={`flex w-full items-baseline gap-4 py-[13px] text-start transition-colors duration-300 ${
                  it.grand
                    ? "my-2 border border-plum bg-plum px-4 text-white hover:bg-[#C8102E]"
                    : active === i
                      ? "text-blush"
                      : "text-white/75 hover:text-white"
                }`}
              >
                <span className={`w-7 shrink-0 font-sans text-[0.75rem] tabular-nums tracking-[0.18em] ${it.grand ? "text-white/85" : "text-copper"}`}>{it.n}</span>
                <span className="font-serif text-[1.05rem] leading-snug">{it.title}</span>
              </button>
            </li>
          ))}
        </ul>
        {/* Détail : panneau sticky */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <DetailPanel item={items[active]} seeAll={c.ui.seeAll} />
        </div>
      </div>

      <MobileAccordion items={items} />
      <Reveal className="mt-12 max-w-3xl lg:hidden">
        <Link to="/categories" data-testid="categories-see-all-mobile" className="link-line text-[0.75rem] uppercase tracking-[0.3em] text-copper">
          {c.ui.seeAll} <ArrowUpRight size={13} strokeWidth={1.4} className="mb-0.5 inline" />
        </Link>
      </Reveal>
      <Reveal className="mt-14 max-w-3xl">
        <p className="text-[0.95rem] leading-relaxed text-white/70">{cat.nb}</p>
      </Reveal>
    </V3Section>
  );
};
